import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip auth check on public pages
    if (!window.location.pathname.startsWith('/admin')) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      // Try cookie-based auth first
      const { data } = await axios.get(`${config.API}/auth/me`);
      setUser(data);
    } catch {
      // Try token from localStorage
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          const { data } = await axios.get(`${config.API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(data);
        } catch {
          localStorage.removeItem('admin_token');
          setUser(false);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${config.API}/auth/login`, { email, password });
    if (data.token) localStorage.setItem('admin_token', data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${config.API}/auth/logout`, {});
    } catch (err) { if (process.env.NODE_ENV === 'development') console.warn(err); }
    localStorage.removeItem('admin_token');
    setUser(false);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAuthHeaders, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
